import { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import {
  X, ChevronRight, ChevronLeft, Sparkles, PartyPopper,
  Briefcase, AlertTriangle, Repeat, Gift, ShoppingCart, Check, Rocket,
} from "lucide-react";

const STORAGE_KEY = "maxemployee.onboarding_wizard_completed";

const ONBOARDING_STEPS = [
  {
    id: "welcome",
    icon: PartyPopper,
    title: "Welcome to the Marketplace!",
    description: "Let's find the perfect skills for your business. Takes less than 1 minute.",
    imageEmoji: "🚀",
  },
  {
    id: "area",
    icon: Briefcase,
    title: "Business Area",
    description: "Select the area that best represents your business:",
    imageEmoji: "🏢",
    options: [
      { value: "atendimento", label: "Customer Support" },
      { value: "vendas", label: "Sales & Commercial" },
      { value: "marketing", label: "Marketing & Content" },
      { value: "financeiro", label: "Finance & Admin" },
      { value: "rh", label: "HR & People" },
      { value: "tech", label: "Technology & Dev" },
    ],
  },
  {
    id: "problem",
    icon: AlertTriangle,
    title: "Biggest Challenge",
    description: "What is the biggest challenge you face today?",
    imageEmoji: "⚡",
    options: [
      { value: "response_time", label: "Very slow response time" },
      { value: "manual_tasks", label: "Repetitive manual tasks" },
      { value: "lead_qualification", label: "Lead qualification" },
      { value: "scalability", label: "Scaling support" },
      { value: "data_analysis", label: "Time-consuming data analysis" },
    ],
  },
  {
    id: "tasks",
    icon: Repeat,
    title: "Repetitive Tasks",
    description: "Which tasks take up most of your team's time?",
    imageEmoji: "🔄",
    options: [
      { value: "faq", label: "Answering FAQs" },
      { value: "followup", label: "Customer follow-up" },
      { value: "reports", label: "Generating reports" },
      { value: "scheduling", label: "Scheduling meetings" },
      { value: "onboarding", label: "Customer onboarding" },
    ],
  },
  {
    id: "result",
    icon: Gift,
    title: "Your Recommended Skills",
    description: "Based on your answers, we've selected the best skills for you:",
    imageEmoji: "🎯",
  },
];

// Recommendation engine based on answers
function getRecommendations(answers) {
  const freeSkills = [];
  const paidSkills = [];

  // Always include Dynamic FAQ as free
  freeSkills.push({
    name: "Dynamic FAQ",
    price: 0,
    reason: "Automatically answers frequently asked questions",
  });

  if (answers.area === "atendimento" || answers.problem === "response_time" || answers.problem === "scalability" || answers.tasks === "faq") {
    freeSkills.push({
      name: "Auto Responder",
      price: 0,
      reason: "24/7 automatic responses on WhatsApp",
    });
    paidSkills.push({
      name: "Smart Escalation AI",
      price: 49.90,
      reason: "Escalates to humans when necessary",
      trial: 7,
    });
  }

  if (answers.area === "vendas" || answers.problem === "lead_qualification" || answers.tasks === "followup") {
    freeSkills.push({
      name: "Lead Scoring Basic",
      price: 0,
      reason: "Automatically qualifies leads",
    });
    paidSkills.push({
      name: "Sales Follow-up Pro",
      price: 79.90,
      reason: "AI-powered smart follow-up",
      trial: 7,
    });
  }

  if (answers.area === "marketing" || answers.tasks === "reports") {
    freeSkills.push({
      name: "Content Repurposer",
      price: 0,
      reason: "Repurposes content into multiple formats",
    });
    paidSkills.push({
      name: "Campaign Analyzer AI",
      price: 69.90,
      reason: "Analyzes campaigns with AI",
      trial: 14,
    });
  }

  if (answers.area === "financeiro" || answers.tasks === "reports") {
    freeSkills.push({
      name: "Invoice Extractor",
      price: 0,
      reason: "Extracts data from invoices"
    });
    paidSkills.push({
      name: "Finance Dashboard AI",
      price: 89.90,
      reason: "Financial dashboard with AI insights",
      trial: 7,
    });
  }

  if (answers.area === "rh" || answers.tasks === "onboarding") {
    freeSkills.push({
      name: "Onboarding Checklist",
      price: 0,
      reason: "Automated onboarding checklist"
    });
    paidSkills.push({
      name: "HR Assistant AI",
      price: 59.90,
      reason: "Intelligent HR assistant",
      trial: 7,
    });
  }

  if (answers.area === "tech" || answers.problem === "manual_tasks") {
    freeSkills.push({
      name: "Code Review Helper",
      price: 0,
      reason: "Assists with code reviews"
    });
    paidSkills.push({
      name: "DevOps Automator",
      price: 99.90,
      reason: "AI-powered DevOps automations",
      trial: 14,
    });
  }

  // Fallback: ensure at least 3 free + 2 paid
  while (freeSkills.length < 3) {
    const fallbacks = [
      { name: "WhatsApp Auto Reply", price: 0, reason: "Automatic replies" },
      { name: "Meeting Summarizer", price: 0, reason: "Meeting summaries" },
      { name: "Task Prioritizer", price: 0, reason: "Task prioritization" },
    ];
    const fb = fallbacks[freeSkills.length - 1];
    if (fb) freeSkills.push(fb);
    else break;
  }

  while (paidSkills.length < 2) {
    const fallbacks = [
      { name: "Analytics Pro", price: 49.90, reason: "Advanced analytics", trial: 7 },
      { name: "Multi-channel Hub", price: 89.90, reason: "Multi-channel integration", trial: 14 },
    ];
    const fb = fallbacks[paidSkills.length];
    if (fb) paidSkills.push(fb);
    else break;
  }

  return { freeSkills: freeSkills.slice(0, 3), paidSkills: paidSkills.slice(0, 2) };
}

function ProgressBar({ current, total }) {
  const pct = total > 1 ? (current / (total - 1)) * 100 : 100;
  return (
    <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />

    </div>
  );
}

export default function OnboardingWizard({ onComplete, onFirstPurchase }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState("next");
  const [isAnimating, setIsAnimating] = useState(false);
  const [recommendations, setRecommendations] = useState(null);

  useEffect(() => {
    try {
      const completed = localStorage.getItem(STORAGE_KEY);
      if (completed !== "true") {
        const timer = setTimeout(() => setIsOpen(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  const totalSteps = ONBOARDING_STEPS.length;
  const step = ONBOARDING_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  const selectOption = useCallback((value) => {
    setAnswers((prev) => ({ ...prev, [step.id]: value }));
  }, [step.id]);

  const goNext = useCallback(() => {
    if (isAnimating) return;
    if (isLast) {
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch { /* ignore */ }
      setIsOpen(false);
      onComplete?.();
    } else {
      // If entering result step, compute recommendations
      if (ONBOARDING_STEPS[currentStep + 1]?.id === "result") {
        setRecommendations(getRecommendations(answers));
      }
      setDirection("next");
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep((s) => s + 1);
        setIsAnimating(false);
      }, 150);
    }
  }, [currentStep, isLast, isAnimating, answers, onComplete]);

  const goPrev = useCallback(() => {
    if (isAnimating || isFirst) return;
    setDirection("prev");
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => s - 1);
      setIsAnimating(false);
    }, 150);
  }, [isFirst, isAnimating]);

  const skipWizard = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch { /* ignore */ }
    setIsOpen(false);
    onComplete?.();
  }, [onComplete]);

  const activateAll = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch { /* ignore */ }
    setIsOpen(false);
    onComplete?.();
  }, [onComplete]);

  const startTrial = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch { /* ignore */ }
    setIsOpen(false);
    onFirstPurchase?.();
  }, [onFirstPurchase]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Escape") {
        e.preventDefault();
        skipWizard();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, goNext, goPrev, skipWizard]);

  if (!isOpen) return null;

  const StepIcon = step.icon;
  const hasAnswer = step.options ? answers[step.id] : true;
  const canProceed = isFirst || hasAnswer || isLast;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={skipWizard} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-zinc-950 border border-zinc-700 shadow-2xl shadow-black/80 rounded-sm overflow-hidden">
        {/* Top accent */}
        <div className="h-1 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500" />

        {/* Close button */}
        <button
          onClick={skipWizard}
          className="absolute top-4 right-4 p-1.5 text-zinc-500 hover:text-white transition-colors z-10"
          aria-label={t('onboarding_wizard.close')}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>

          {/* Progress bar */}
          <ProgressBar current={currentStep} total={totalSteps} />

          {/* Step indicators */}
          <div className="flex items-center justify-between mt-4 mb-6">
            {ONBOARDING_STEPS.map((s, i) => {
              const SIcon = s.icon;
              return (
                <div key={s.id} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-sm border transition-all ${
                      i === currentStep
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                        : i < currentStep
                        ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                        : "bg-zinc-900 border-zinc-700 text-zinc-600"
                    }`}
                  >
                    <SIcon className="w-4 h-4" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div
            className={`text-center py-6 transition-all duration-150 ${
              isAnimating
                ? direction === "next"
                  ? "opacity-0 translate-x-4"
                  : "opacity-0 -translate-x-4"
                : "opacity-100 translate-x-0"
            }`}
          >
            <div className="text-5xl mb-4">{step.imageEmoji}</div>
            <h3 className="font-display text-xl uppercase text-white mb-2">
              {step.title}
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-md mx-auto mb-6">
              {step.description}
            </p>

            {/* Options for steps 2-4 */}
            {step.options && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg mx-auto">
                {step.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => selectOption(opt.value)}
                    className={`text-left px-4 py-3 rounded-sm border text-sm transition-all ${
                      answers[step.id] === opt.value
                        ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                        : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Result step with recommendations */}
            {isLast && recommendations && (
              <div className="max-w-lg mx-auto text-left">
                {/* Free skills */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">{t('onboarding_wizard.free_skills')}</span>
                  </div>
                  {recommendations.freeSkills.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-4 py-3 mb-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm"
                    >
                      <div>
                        <div className="text-sm text-white font-medium">{s.name}</div>
                        <div className="text-xs text-zinc-500">{s.reason}</div>
                      </div>
                      <span className="text-emerald-400 font-mono text-xs font-bold">FREE</span>
                    </div>
                  ))}
                </div>

                {/* Paid skills with trial */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="w-4 h-4 text-amber-400" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400">{t('onboarding_wizard.skills_with_trial')}</span>
                  </div>
                  {recommendations.paidSkills.map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-4 py-3 mb-2 bg-amber-500/5 border border-amber-500/20 rounded-sm"
                    >
                      <div>
                        <div className="text-sm text-white font-medium">{s.name}</div>
                        <div className="text-xs text-zinc-500">{s.reason}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-amber-400 font-mono text-xs font-bold">
                          ${s.price.toFixed(2)}/mo
                        </div>
                        <div className="text-[10px] text-zinc-500">{s.trial} days free</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={activateAll}
                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors rounded-sm"
                  >
                    <Check className="w-4 h-4" />
                    Activate all
                  </button>
                  <button
                    onClick={startTrial}
                    className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs transition-colors rounded-sm"
                  >
                    <Rocket className="w-4 h-4" />
                    Start trial
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-zinc-800">
            <button
              onClick={skipWizard}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition-colors px-3 py-2"
            >{t('onboarding_wizard.skip')}</button>

            <div className="flex items-center gap-3">
              {!isFirst && (
                <button
                  onClick={goPrev}
                  className="flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              {!isLast && (
                <button
                  onClick={goNext}
                  disabled={!canProceed}
                  className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-6 py-2.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
                >
                  {isFirst ? "Start" : "Next"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Utility to check if wizard was completed
export function isOnboardingWizardCompleted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

// Utility to reset wizard state (for testing)
export function resetOnboardingWizard() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

// Utility to trigger wizard programmatically
export function triggerOnboardingWizard() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  } catch { /* ignore */ }
}
